import { InvoiceParams } from 'models/Invoice'

import { Record as CustomerChargeRecord } from 'components/Modal/ModalInvoice/components/Table/Table'

// Não permite emitir nota fiscal para cobranças expiradas/em cancelamento.
const NON_EMITTABLE_NFSE_STATUSES = [
  'expiring',
  'no_payment',
  'cancelling',
  'cancelled'
]

export type NfseEmissionEligibility = {
  /** `nfse_automation_active` da billing: nota automática dispensa a manual. */
  nfseAutomationActive?: boolean
  linkedNfse?: unknown
  canLinkNfse?: boolean
  status?: string
}

export const isNfseEmittableStatus = (status?: string | null): boolean =>
  !NON_EMITTABLE_NFSE_STATUSES.includes(status ?? '')

/**
 * A cobrança aceita uma nota avulsa emitida e vinculada a ela? Mesma regra do
 * backend (Nfse::LinkBilling): só sem automação de NFSe (nfse_policy no_nfse ou
 * on_demand), sem nota já vinculada e fora dos status que não emitem.
 */
export const canEmitManualNfse = (
  billing?: NfseEmissionEligibility | null
): boolean =>
  !!billing &&
  billing.nfseAutomationActive === false &&
  !billing.linkedNfse &&
  !!billing.canLinkNfse &&
  isNfseEmittableStatus(billing.status)

/**
 * Linha da cobrança fixada na tabela de vínculo do modal de nota fiscal, a partir
 * do detalhe da cobrança (`/v1/billings/:uuid/details`). `overrides` cobre o que
 * acabou de mudar e ainda não voltou do backend (ex.: a baixa recém-registrada).
 */
export const buildNfseChargeFromInvoice = (
  invoice?: InvoiceParams | null,
  overrides?: Partial<CustomerChargeRecord>
): CustomerChargeRecord | undefined =>
  invoice
    ? {
        uuid: invoice.uuid,
        customer: {
          name: invoice.customer?.name ?? '',
          nickname: invoice.customer?.nickname ?? '',
          uuid: invoice.customer?.uuid ?? ''
        },
        due_date: invoice.dueDate,
        date_paid: invoice.datePaid ?? null,
        amount_paid: invoice.amountPaid ?? '0.0',
        amount_billed: invoice.amountBilled,
        status: invoice.status,
        viewed: invoice.viewed,
        recurrence_uuid: invoice.recurrenceUuid ?? '',
        nfse_status: undefined as any,
        nfse: undefined as any,
        nfse_policy: invoice.nfsePolicy ?? '',
        ...overrides
      }
    : undefined

type DirectPayRecord = {
  uuid: string
  due_date: string
  amount_billed: string | number
  viewed?: boolean
  customer?: { uuid?: string; name?: string; nickname?: string }
}

/**
 * Mesma linha, a partir do `record` devolvido por `POST /v1/billings/direct_pay`.
 * O recebimento nasce pago e sem automação de nota (nfse_policy no_nfse); valor
 * e data do pagamento vêm do formulário, porque o `record` é serializado antes
 * de o pagamento atualizar a cobrança.
 */
export const buildNfseChargeFromDirectPay = (
  record: DirectPayRecord,
  payment: { amountPaid: string; datePaid: string }
): CustomerChargeRecord => ({
  uuid: record.uuid,
  customer: {
    name: record.customer?.name ?? '',
    nickname: record.customer?.nickname ?? '',
    uuid: record.customer?.uuid ?? ''
  },
  due_date: record.due_date,
  date_paid: payment.datePaid,
  amount_paid: payment.amountPaid,
  amount_billed: String(record.amount_billed),
  status: 'paid',
  viewed: !!record.viewed,
  recurrence_uuid: '',
  nfse_status: undefined as any,
  nfse: undefined as any,
  nfse_policy: 'no_nfse'
})
